
import { useState } from 'react';
import { useBroker } from '@/hooks/use-broker';
import { useApplications } from '@/hooks/use-applications';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { CopyIcon, CheckIcon, UsersIcon, FileTextIcon } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { LinkedUsersList } from './LinkedUsersList';
import { LinkedUsersApplications } from './LinkedUsersApplications';

export const BrokerDashboard = () => {
  const { brokerProfile, linkedUsers, isLoading: isLoadingBroker } = useBroker();
  const { applications, isLoading: isLoadingApplications } = useApplications();
  const [copied, setCopied] = useState(false);
  const { toast } = useToast();
  
  const handleCopyReferralCode = () => {
    if (!brokerProfile?.referral_code) return;
    
    navigator.clipboard.writeText(brokerProfile.referral_code);
    setCopied(true);
    toast({
      title: "Referral code copied",
      description: "The referral code has been copied to your clipboard"
    });
    
    setTimeout(() => setCopied(false), 2000);
  };
  
  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-3xl font-bold">Broker Dashboard</h2>
      </div>
      
      <Card>
        <CardHeader className="pb-2">
          <CardTitle>Your Referral Code</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex space-x-2 items-center">
            <Input
              value={brokerProfile?.referral_code || ''}
              readOnly
              className="font-mono bg-muted"
            />
            <Button 
              variant="outline" 
              size="icon" 
              onClick={handleCopyReferralCode}
              className="shrink-0"
            >
              {copied ? <CheckIcon className="h-4 w-4" /> : <CopyIcon className="h-4 w-4" />}
            </Button>
          </div>
          <p className="text-sm text-muted-foreground mt-2">
            Share this code with your clients so they can link their account to you
          </p>
        </CardContent>
      </Card>
      
      <Tabs defaultValue="users">
        <TabsList className="mb-4">
          <TabsTrigger value="users" className="flex items-center">
            <UsersIcon className="h-4 w-4 mr-2" />
            Linked Clients
          </TabsTrigger>
          <TabsTrigger value="applications" className="flex items-center">
            <FileTextIcon className="h-4 w-4 mr-2" />
            Client Applications
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="users">
          <LinkedUsersList linkedUsers={linkedUsers} isLoading={isLoadingBroker} />
        </TabsContent>
        
        <TabsContent value="applications">
          <LinkedUsersApplications 
            linkedUsers={linkedUsers} 
            applications={applications} 
            isLoading={isLoadingBroker || isLoadingApplications} 
          />
        </TabsContent>
      </Tabs>
    </div>
  );
};
